<template>
  <transition name="modal">
    <div v-if="isOpen" class="modal-overlay" @click.self="$emit('close')">
      <div class="settings-modal">
        <!-- Header -->
        <div class="modal-header">
          <div class="header-title">
            <div class="icon-wrapper">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </div>
            <span>{{ language === 'ar' ? 'الإعدادات' : 'Einstellungen' }}</span>
          </div>
          <button class="close-btn" @click="$emit('close')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="modal-body">
          <!-- Language Selection -->
          <div class="setting-group">
            <label class="group-label">{{ language === 'ar' ? 'اللغة' : 'Sprache' }}</label>
            <div class="language-toggle">
              <button
                :class="['lang-btn', { active: language === 'de' }]"
                @click="setLanguage('de')"
              >
                <span class="flag">🇩🇪</span>
                <span>Deutsch</span>
              </button>
              <button
                :class="['lang-btn', { active: language === 'ar' }]"
                @click="setLanguage('ar')"
              >
                <span class="flag">🇸🇦</span>
                <span>العربية</span>
              </button>
            </div>
          </div>

          <!-- Sound Toggle -->
          <div class="setting-group">
            <div class="setting-row">
              <div class="setting-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <template v-if="soundEnabled">
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                  </template>
                  <template v-else>
                    <line x1="23" y1="9" x2="17" y2="15"/>
                    <line x1="17" y1="9" x2="23" y2="15"/>
                  </template>
                </svg>
                <span>{{ language === 'ar' ? 'الصوت' : 'Sound' }}</span>
              </div>
              <button :class="['switch', { on: soundEnabled }]" @click="toggleSound">
                <span class="switch-handle"></span>
              </button>
            </div>
          </div>

          <!-- Article Number Toggle -->
          <div class="setting-group">
            <div class="setting-row">
              <div class="setting-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 7h16M4 12h16M4 17h10"/>
                </svg>
                <span>{{ language === 'ar' ? 'رقم المقال' : 'Artikelnummer' }}</span>
              </div>
              <button :class="['switch', { on: showArticleNumber }]" @click="toggleArticleNumber">
                <span class="switch-handle"></span>
              </button>
            </div>
          </div>

          <!-- Dark Mode Toggle -->
          <div class="setting-group">
            <div class="setting-row">
              <div class="setting-info">
                <svg v-if="darkMode" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/>
                  <line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/>
                  <line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
                <span>{{ language === 'ar' ? 'الوضع الداكن' : 'Dunkelmodus' }}</span>
              </div>
              <button :class="['switch', { on: darkMode }]" @click="toggleTheme">
                <span class="switch-handle"></span>
              </button>
            </div>
          </div>

          <!-- Sound Test Button -->
          <div class="setting-group" v-if="soundEnabled">
            <div class="setting-row sound-test-row" :class="{ 'sound-blocked': !audioUnlocked }">
              <div class="setting-info">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <template v-if="audioUnlocked">
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                  </template>
                  <template v-else>
                    <line x1="23" y1="9" x2="17" y2="15"/>
                    <line x1="17" y1="9" x2="23" y2="15"/>
                  </template>
                </svg>
                <span>{{ language === 'ar' ? 'اختبار الصوت' : 'Sound testen' }}</span>
              </div>
              <button class="test-sound-btn" :class="{ 'unlocked': audioUnlocked }" @click="testSound">
                <span v-if="audioUnlocked">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                </span>
                <span v-else>{{ language === 'ar' ? 'تفعيل' : 'Aktivieren' }}</span>
              </button>
            </div>
            <p v-if="!audioUnlocked" class="sound-hint">
              {{ language === 'ar' ? 'اضغط لتفعيل الصوت' : 'Klicken um Sound zu aktivieren' }}
            </p>
          </div>

          <!-- Divider -->
          <div class="divider"></div>

          <!-- Quick Actions -->
          <div class="quick-actions">
            <!-- Uber Order Modal Preview -->
            <button class="action-card" @click="$emit('previewUberModal')">
              <div class="action-icon uber">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div class="action-text">
                <span class="action-title">Uber Eats Modal</span>
                <span class="action-desc">{{ language === 'ar' ? 'معاينة نافذة الطلب' : 'Bestellfenster Vorschau' }}</span>
              </div>
              <svg class="chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>

            <button class="action-card" @click="$emit('showArchive')">
              <div class="action-icon archive">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div class="action-text">
                <span class="action-title">{{ language === 'ar' ? 'الأرشيف' : 'Archiv' }}</span>
                <span class="action-desc">{{ language === 'ar' ? 'عرض الطلبات السابقة' : 'Vergangene Bestellungen' }}</span>
              </div>
              <svg class="chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>

            <button class="action-card" @click="$emit('changeStation')">
              <div class="action-icon station">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="17 1 21 5 17 9"/>
                  <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                  <polyline points="7 23 3 19 7 15"/>
                  <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                </svg>
              </div>
              <div class="action-text">
                <span class="action-title">{{ language === 'ar' ? 'تغيير المحطة' : 'Station wechseln' }}</span>
                <span class="action-desc">{{ language === 'ar' ? 'اختر محطة أخرى' : 'Andere Station wählen' }}</span>
              </div>
              <svg class="chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button class="logout-btn" @click="$emit('logout')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span>{{ language === 'ar' ? 'تسجيل الخروج' : 'Abmelden' }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'SettingsModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    audioUnlocked: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(['language', 'soundEnabled', 'showArticleNumber', 'darkMode'])
  },
  methods: {
    ...mapActions(['toggleSound', 'toggleArticleNumber', 'toggleTheme']),
    setLanguage(lang) {
      if (this.language !== lang) {
        this.$store.dispatch('toggleLanguage');
      }
    },
    testSound() {
      this.$emit('testSound');
    }
  }
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity .2s ease; }
.modal-enter-active .settings-modal, .modal-leave-active .settings-modal { transition: transform .2s ease, opacity .2s ease; }
.modal-enter, .modal-leave-to { opacity: 0; }
.modal-enter .settings-modal, .modal-leave-to .settings-modal { transform: translateY(8px); opacity: 0; }
.modal-overlay { position: fixed; inset: 0; background: var(--ods-overlay); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.settings-modal { background: var(--ods-surface); border-radius: var(--ods-radius-lg); width: 100%; max-width: 400px; border: 1px solid var(--ods-line); box-shadow: var(--ods-shadow-lg); overflow: hidden; color: var(--ods-ink); }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid var(--ods-line); }
.header-title { display: flex; align-items: center; gap: 12px; }
.icon-wrapper { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; background: var(--ods-accent-tint); border-radius: var(--ods-radius); color: var(--ods-accent-ink); }
.header-title span { font-size: 17px; font-weight: 800; color: var(--ods-ink); }
.close-btn { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; background: var(--ods-surface-2); border: 1px solid var(--ods-line); border-radius: var(--ods-radius); color: var(--ods-ink-2); cursor: pointer; }
.close-btn:hover { background: var(--ods-surface-3); color: var(--ods-ink); }
.modal-body { padding: 18px 20px; }
.setting-group { margin-bottom: 14px; }
.group-label { display: block; font-size: 11px; font-weight: 800; color: var(--ods-ink-3); text-transform: uppercase; letter-spacing: .07em; margin-bottom: 8px; }
.language-toggle { display: flex; gap: 8px; }
.lang-btn { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; height: 46px; background: var(--ods-surface-2); border: 1px solid var(--ods-line-2); border-radius: var(--ods-radius); cursor: pointer; transition: all .15s; }
.lang-btn:hover { background: var(--ods-surface-3); }
.lang-btn.active { background: var(--ods-accent-tint); border-color: var(--ods-accent); }
.lang-btn .flag { font-size: 18px; }
.lang-btn span:last-child { font-size: 14px; font-weight: 700; color: var(--ods-ink-2); }
.lang-btn.active span:last-child { color: var(--ods-ink); }
.setting-row { display: flex; align-items: center; justify-content: space-between; height: 52px; padding: 0 14px; background: var(--ods-surface-2); border: 1px solid var(--ods-line); border-radius: var(--ods-radius); }
.setting-info { display: flex; align-items: center; gap: 12px; color: var(--ods-ink-2); }
.setting-info span { font-size: 14px; font-weight: 700; color: var(--ods-ink); }
.switch { position: relative; width: 46px; height: 26px; background: var(--ods-line-2); border: none; border-radius: 13px; cursor: pointer; transition: background .15s; }
.switch.on { background: var(--ods-accent); }
.switch-handle { position: absolute; top: 3px; left: 3px; width: 20px; height: 20px; background: #fff; border-radius: 50%; transition: transform .15s; box-shadow: 0 1px 2px rgba(0,0,0,.25); }
.switch.on .switch-handle { transform: translateX(20px); }
.divider { height: 1px; background: var(--ods-line); margin: 16px 0; }
.quick-actions { display: flex; flex-direction: column; gap: 8px; }
.action-card { display: flex; align-items: center; gap: 12px; padding: 10px 12px; background: var(--ods-surface); border: 1px solid var(--ods-line); border-radius: var(--ods-radius); cursor: pointer; transition: background .15s, border-color .15s; text-align: left; }
.action-card:hover { background: var(--ods-surface-2); border-color: var(--ods-line-2); }
.action-icon { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; border-radius: var(--ods-radius); flex-shrink: 0; }
.action-icon.uber { background: rgba(6, 193, 103, .14); color: var(--ods-uber); }
.action-icon.archive { background: var(--ods-prep-tint); color: var(--ods-prep-ink); }
.action-icon.station { background: var(--ods-accent-tint); color: var(--ods-accent-ink); }
.action-text { flex: 1; display: flex; flex-direction: column; gap: 1px; }
.action-title { font-size: 14px; font-weight: 800; color: var(--ods-ink); }
.action-desc { font-size: 12px; color: var(--ods-ink-3); font-weight: 600; }
.chevron { color: var(--ods-ink-3); }
.modal-footer { padding: 14px 20px 18px; border-top: 1px solid var(--ods-line); }
.logout-btn { width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; height: 46px; background: transparent; border: 1px solid var(--ods-danger); border-radius: var(--ods-radius); color: var(--ods-danger-ink); font-size: 14px; font-weight: 700; cursor: pointer; }
.logout-btn:hover { background: var(--ods-danger-tint); }
.sound-test-row.sound-blocked { border-color: var(--ods-prep); background: var(--ods-prep-tint); }
.test-sound-btn { height: 36px; padding: 0 14px; border-radius: var(--ods-radius); border: none; font-size: 13px; font-weight: 800; cursor: pointer; display: flex; align-items: center; justify-content: center; font-family: inherit; }
.test-sound-btn:not(.unlocked) { background: var(--ods-prep); color: #fff; }
.test-sound-btn.unlocked { background: var(--ods-ok-tint); color: var(--ods-ok-ink); width: 40px; padding: 0; }
.sound-hint { font-size: 11px; color: var(--ods-prep-ink); margin-top: 6px; text-align: center; font-weight: 600; }
</style>
